import { useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    PendoId,
    PendoProps,
    Section,
    Tag,
    TSTAdjustView,
    TSTPagination,
    TSTTable,
} from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { ProgramOverview, WaitingOnCounts } from "api/generated/model";
import { TagsCell } from "components/tagsCell/TagsCell";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import { EventType } from "utils/controlResults/activityStatusUtils";

import * as theme from "./ControlHealthStatusBreakdownTable.css";
import { OverdueColumnHeader } from "./OverdueColumnHeader";
import {
    overdueColumnLabel,
    OverdueRange,
    OverdueRanges,
    statusVisibilityStorage,
} from "./overdueColumns";
import { CONTROL_HEALTH_PENDO } from "./pendo";
import { useBuildStatusBreakdownUrl } from "./useBuildStatusBreakdownUrl";
import { useProgramOverviewColumns } from "./useProgramOverviewColumns";

interface ControlHealthStatusBreakdownTableProps {
    isNewDashboard: boolean;
    programs: ProgramOverview[];
    rangeLabel: string;
    overdueRanges: OverdueRanges;
    onOverdueRangesChange: (ranges: OverdueRanges) => void;
}

const columnHelper = createColumnHelper<ProgramOverview>();

export function ControlHealthStatusBreakdownTable({
    isNewDashboard,
    programs,
    rangeLabel,
    overdueRanges,
    onOverdueRangesChange,
}: ControlHealthStatusBreakdownTableProps) {
    const { t } = useTranslation();
    const buildActivitiesUrl = useBuildStatusBreakdownUrl();
    const tableRef = useRef<HTMLDivElement>(null);
    const visiblePrograms = useMemo(
        () =>
            isNewDashboard
                ? programs
                : programs.filter((program) => ongoingCount(program) > 0),
        [programs, isNewDashboard]
    );
    const [columnVisibility, setColumnVisibility] = useState(() =>
        isNewDashboard
            ? statusVisibilityStorage.getOr("statusColumnVisibility", {})
            : {}
    );
    useEffect(() => {
        if (isNewDashboard) {
            statusVisibilityStorage.set(
                "statusColumnVisibility",
                columnVisibility
            );
        }
    }, [columnVisibility, isNewDashboard]);
    const overview = useProgramOverviewColumns({
        buildActivitiesUrl,
    });
    const columns = useMemo(
        () => [
            columnHelper.accessor("controlProgramName", {
                header: t("ControlResult_ControlProgram"),
                size: 220,
                meta: {
                    headerPendoId: CONTROL_HEALTH_PENDO.sortProgram,
                },
                cell: ({ row, getValue }) => (
                    <a
                        data-pendo={CONTROL_HEALTH_PENDO.programLink}
                        href={buildControlProgramViewUrl(
                            row.original.controlProgramId
                        )}
                    >
                        {getValue()}
                    </a>
                ),
            }),
            columnHelper.accessor(
                (program) =>
                    program.tags
                        .map(({ category, name }) => `${category}: ${name}`)
                        .join(", "),
                {
                    id: "tags",
                    header: t("Tags"),
                    size: 150,
                    meta: {
                        headerPendoId: CONTROL_HEALTH_PENDO.sortTags,
                    },
                    cell: ({ row }) => <TagsCell tags={row.original.tags} />,
                }
            ),
            ...(isNewDashboard
                ? overview.columns
                : [
                      columnHelper.accessor(ongoingCount, {
                          id: "total",
                          header: t("ControlHealth_StatusBreakdown_Ongoing"),
                          meta: {
                              fixedWidth: true,
                              headerPendoId: CONTROL_HEALTH_PENDO.sortOngoing,
                          },
                          size: 170,
                          cell: ({ row }) => {
                              const program = row.original;
                              return (
                                  <div className={theme.chips}>
                                      <CountChip
                                          href={buildActivitiesUrl(
                                              program.controlProgramId,
                                              ["Pending", "Overdue"]
                                          )}
                                          tone="total"
                                          data-pendo={
                                              CONTROL_HEALTH_PENDO.chipTotal
                                          }
                                          label={t(
                                              "ControlHealth_StatusBreakdown_Total",
                                              {
                                                  count: ongoingCount(program),
                                              }
                                          )}
                                      />
                                      <CountChip
                                          href={buildActivitiesUrl(
                                              program.controlProgramId,
                                              "Pending"
                                          )}
                                          tone="pending"
                                          data-pendo={
                                              CONTROL_HEALTH_PENDO.chipInProgress
                                          }
                                          dot
                                          label={t(
                                              "ControlHealth_StatusBreakdown_InProgress",
                                              {
                                                  count: program.activities
                                                      .pending,
                                              }
                                          )}
                                      />
                                      <CountChip
                                          href={buildActivitiesUrl(
                                              program.controlProgramId,
                                              "Overdue"
                                          )}
                                          tone="overdue"
                                          data-pendo={
                                              CONTROL_HEALTH_PENDO.chipOverdue
                                          }
                                          dot
                                          label={t(
                                              "ControlHealth_StatusBreakdown_Overdue",
                                              {
                                                  count: program.activities
                                                      .overdue,
                                              }
                                          )}
                                      />
                                  </div>
                              );
                          },
                      }),
                  ]),
            ...(["under", "atLeast"] as const).map((kind) => {
                const range = overdueRanges[kind];
                return columnHelper.accessor(
                    (program) => sumWaitingOn(program.overdue[kind]),
                    {
                        id: kind,
                        meta: {
                            value: range,
                            onChange: (next: OverdueRange) =>
                                onOverdueRangesChange(
                                    kind === "under"
                                        ? {
                                              ...overdueRanges,
                                              under: {
                                                  ...next,
                                                  comparison: {
                                                      ...next.comparison,
                                                      type: "atMost",
                                                  },
                                              },
                                          }
                                        : {
                                              ...overdueRanges,
                                              atLeast: {
                                                  ...next,
                                                  comparison: {
                                                      ...next.comparison,
                                                      type: "atLeast",
                                                  },
                                              },
                                          }
                                ),
                            fixedWidth: true,
                            plainLabel: overdueColumnLabel(range, t),
                            headerPendoId:
                                kind === "under"
                                    ? CONTROL_HEALTH_PENDO.sortUnderSevenDays
                                    : CONTROL_HEALTH_PENDO.sortSevenDaysOrMore,
                        },
                        header: isNewDashboard
                            ? OverdueColumnHeader
                            : t(
                                  kind === "under"
                                      ? "ControlHealth_StatusBreakdown_UnderSevenDays"
                                      : "ControlHealth_StatusBreakdown_SevenDaysOrMore"
                              ),
                        size: isNewDashboard ? 240 : 170,
                        cell: ({ row }) => (
                            <WaitingOnChips
                                getHref={(status) =>
                                    buildActivitiesUrl(
                                        row.original.controlProgramId,
                                        status,
                                        range
                                    )
                                }
                                counts={row.original.overdue[kind]}
                                pendoIds={
                                    kind === "under"
                                        ? {
                                              responsible:
                                                  CONTROL_HEALTH_PENDO.chipUnderSevenDaysResponsible,
                                              reviewer:
                                                  CONTROL_HEALTH_PENDO.chipUnderSevenDaysReviewer,
                                              secondReviewer:
                                                  CONTROL_HEALTH_PENDO.chipUnderSevenDaysSecondReviewer,
                                          }
                                        : {
                                              responsible:
                                                  CONTROL_HEALTH_PENDO.chipSevenDaysOrMoreResponsible,
                                              reviewer:
                                                  CONTROL_HEALTH_PENDO.chipSevenDaysOrMoreReviewer,
                                              secondReviewer:
                                                  CONTROL_HEALTH_PENDO.chipSevenDaysOrMoreSecondReviewer,
                                          }
                                }
                            />
                        ),
                    }
                );
            }),
        ],
        [
            t,
            buildActivitiesUrl,
            overview.columns,
            overdueRanges,
            onOverdueRangesChange,
            isNewDashboard,
        ]
    );
    const table = useReactTable({
        data: visiblePrograms,
        columns,
        getRowId: (program) => String(program.controlProgramId),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableSortingRemoval: false,
        state: { columnVisibility },
        onColumnVisibilityChange: setColumnVisibility,
        initialState: {
            sorting: [
                { id: isNewDashboard ? "activities" : "total", desc: true },
            ],
            pagination: { pageIndex: 0, pageSize: 12 },
        },
    });

    return (
        <Section
            className={theme.card}
            data-pendo={CONTROL_HEALTH_PENDO.statusCard}
        >
            <div className={theme.header}>
                <div className={theme.heading}>
                    <h2 className={theme.title}>
                        {t("ControlHealth_StatusBreakdown_Title")}
                    </h2>
                    {rangeLabel && (
                        <p className={theme.subtitle}>
                            {t("ControlHealth_StatusBreakdown_Subtitle", {
                                range: rangeLabel,
                            })}
                        </p>
                    )}
                </div>
                {isNewDashboard && overview.controls}
                <TSTAdjustView
                    data-pendo={CONTROL_HEALTH_PENDO.adjustTable}
                    table={table}
                    columnsLabel={t("AdjustView_ColumnsHeader")}
                    reorderHintLabel={t("AdjustView_DragToReorderHint")}
                    resetLabel={t("AdjustView_Reset")}
                    pinLabel={t("AdjustView_PinColumn")}
                    unpinLabel={t("AdjustView_UnpinColumn")}
                    containerRef={tableRef}
                >
                    {t("ControlHealth_StatusBreakdown_AdjustTable")}
                </TSTAdjustView>
            </div>
            <div ref={tableRef}>
                <TSTTable
                    table={table}
                    noResultsFallback={t("ControlHealth_StatusBreakdown_Empty")}
                    noSourceRowsFallback={t(
                        "ControlHealth_StatusBreakdown_Empty"
                    )}
                />
            </div>
            <div className={theme.footer}>
                <TSTPagination
                    table={table}
                    pageLabel={t("Pagination_Page")}
                    getRowCountLabel={(shown, count) =>
                        t("Pagination_RowCount", { shown, count })
                    }
                    variant="inline"
                />
            </div>
        </Section>
    );
}

type CountChipTone = "total" | "pending" | "overdue";

function CountChip({
    href,
    label,
    tone,
    dot = false,
    "data-pendo": dataPendo,
}: {
    href: string;
    label: string;
    tone: CountChipTone;
    // A dot in the status mark colour, so the chip reads like the donut legend.
    dot?: boolean;
} & PendoProps) {
    return (
        <Link to={href} className={theme.countLink} data-pendo={dataPendo}>
            <Tag
                variant="countPill"
                wrap
                className={theme.chipTone[tone]}
                icon={
                    dot &&
                    tone !== "total" && (
                        <span className={theme.statusDot[tone]} />
                    )
                }
            >
                {label}
            </Tag>
        </Link>
    );
}

function WaitingOnChips({
    counts,
    getHref,
    pendoIds,
}: {
    counts: WaitingOnCounts;
    getHref: (status: EventType) => string;
    pendoIds: Record<keyof WaitingOnCounts, PendoId>;
}) {
    const { t } = useTranslation();
    const roles: {
        count: number;
        label: string;
        status: EventType;
        pendoId: PendoId;
    }[] = [
        {
            status: "OverdueOnResponsible",
            pendoId: pendoIds.responsible,
            count: counts.responsible,
            label: t("ControlHealth_StatusBreakdown_Responsible", {
                count: counts.responsible,
            }),
        },
        {
            status: "OverdueOnFirstReviewer",
            pendoId: pendoIds.reviewer,
            count: counts.reviewer,
            label: t("ControlHealth_StatusBreakdown_Reviewer", {
                count: counts.reviewer,
            }),
        },
        {
            status: "OverdueOnSecondReviewer",
            pendoId: pendoIds.secondReviewer,
            count: counts.secondReviewer,
            label: t("ControlHealth_StatusBreakdown_SecondReviewer", {
                count: counts.secondReviewer,
            }),
        },
    ];
    return (
        <div className={theme.waitingOn}>
            {roles
                .filter(({ count }) => count > 0)
                .map(({ label, status, pendoId }) => (
                    <CountChip
                        key={status}
                        data-pendo={pendoId}
                        href={getHref(status)}
                        label={label}
                        tone="overdue"
                    />
                ))}
        </div>
    );
}

function sumWaitingOn(counts: WaitingOnCounts): number {
    return counts.responsible + counts.reviewer + counts.secondReviewer;
}

function ongoingCount(program: ProgramOverview): number {
    return program.activities.pending + program.activities.overdue;
}
